// DeepSeek API service for "Let's think" functionality with progress tracking
const DEEPSEEK_API_KEY = 'sk-6d1c0e29faa046ba857b2fcc15abd3b3';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// DeepSeek API Tariff (as provided in requirements)
const DEEPSEEK_TARIFF = {
  standard: {
    input_cache_hit: 0.5, // 0.5元 per million tokens
    input_cache_miss: 2.0, // 2元 per million tokens
    output: 8.0 // 8元 per million tokens
  },
  discount: {
    input_cache_hit: 0.25, // 0.25元 per million tokens (5折)
    input_cache_miss: 1.0, // 1元 per million tokens (5折)
    output: 4.0 // 4元 per million tokens (5折)
  }
};

class DeepSeekThinkService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  // Calculate if current time is in discount period (00:30-08:30 Beijing time)
  isDiscountPeriod() {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getHours();
    return hour >= 0 && hour < 8.5; // 00:30 to 08:30
  }

  // Calculate cost based on tokens and tariff
  calculateCost(inputTokens, outputTokens, cacheHit = false) {
    const tariff = this.isDiscountPeriod() ? DEEPSEEK_TARIFF.discount : DEEPSEEK_TARIFF.standard;
    
    const inputCost = cacheHit ? 
      (inputTokens / 1000000) * tariff.input_cache_hit :
      (inputTokens / 1000000) * tariff.input_cache_miss;
    
    const outputCost = (outputTokens / 1000000) * tariff.output;
    
    return {
      inputCost: inputCost,
      outputCost: outputCost,
      totalCost: inputCost + outputCost,
      isDiscountPeriod: this.isDiscountPeriod()
    };
  }

  // Retry mechanism for failed requests
  async retryRequest(fetchFunction, maxRetries = 2) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fetchFunction();
      } catch (error) {
        console.log(`Attempt ${attempt} failed:`, error.message);
        
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        console.log(`Retrying in ${delay}ms...`);
        await this.delay(delay);
      }
    }
  }

    async thinkAboutProblem(problem, problemType = 'general', onProgress = null, jsonStructure = null) {
    const startTime = Date.now();
    let inputTokens = 0;
    let outputTokens = 0;
    let progressUpdateInterval = null;

    try {
      // Validate input
      if (!problem || typeof problem !== 'string' || problem.trim() === '') {
        throw new Error('Invalid problem input. Please provide a valid mathematical problem.');
      }

      if (onProgress) {
        onProgress({
          status: 'preparing',
          message: '準備分析數學問題...',
          timeElapsed: 0,
          tokensUsed: 0,
          cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountPeriod: this.isDiscountPeriod() },
          details: 'Initializing AI analysis with template structure'
        });
      }

      // Test API connection first
      if (onProgress) {
        onProgress({
          status: 'connecting',
          message: 'Testing DeepSeek API connection...',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: 0,
          cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountPeriod: this.isDiscountPeriod() },
          details: 'Verifying DeepSeek API connectivity'
        });
      }

      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        console.warn('Initial connection test failed, but proceeding with request...');
        // Don't throw error immediately, try the main request anyway
      }

      if (onProgress) {
        onProgress({
          status: 'connected',
          message: 'DeepSeek API connected successfully',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: 0,
          cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountPeriod: this.isDiscountPeriod() },
          details: 'API connection verified, proceeding with analysis'
        });
      }

      const prompt = this.createThinkPrompt(problem, problemType, jsonStructure);
      
      // Determine problem category for progress updates - improved detection
      let isHKDSE = false;
      let problemCategory = 'Topics';
      
      if (jsonStructure) {
        // Check multiple indicators for HKDSE problems
        isHKDSE = (
          (jsonStructure.problem && jsonStructure.problem.includes('HKDSE')) ||
          (jsonStructure.analysis && jsonStructure.analysis.includes('HKDSE')) ||
          (jsonStructure.problemType && jsonStructure.problemType.includes('HKDSE')) ||
          (jsonStructure.id && jsonStructure.id.includes('hkdse')) ||
          (jsonStructure.graphData && jsonStructure.graphData.nodes && 
           jsonStructure.graphData.nodes.some(node => 
             node.name && node.name.includes('HKDSE')
           ))
        );
        problemCategory = isHKDSE ? 'HKDSE' : 'Topics';
      }
      
      // More accurate token estimation
      inputTokens = Math.ceil(prompt.length / 3.5); // Better estimate: 1 token ≈ 3.5 characters
      
                   // Update progress - sending request
      if (onProgress) {
        onProgress({
          status: 'sending',
          message: 'Sending problem to DeepSeek AI...',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: inputTokens,
          cost: this.calculateCost(inputTokens, 0),
          details: `Using ${problemCategory} template structure for analysis`
        });
      }

             // Simulate some processing time for better UX (like in drill API)
       await this.delay(1000);

       console.log('Sending request to DeepSeek API...');
       
       // Add periodic progress updates during long requests
       if (onProgress) {
         progressUpdateInterval = setInterval(() => {
           const currentTime = Math.floor((Date.now() - startTime) / 1000);
           onProgress({
             status: 'sending',
             message: 'Sending problem to DeepSeek AI...',
             timeElapsed: currentTime,
             tokensUsed: inputTokens + Math.floor(currentTime * 10), // Simulate token growth
             cost: this.calculateCost(inputTokens + Math.floor(currentTime * 10), 0),
             details: `Using ${problemCategory} template structure for analysis`
           });
         }, 5000); // Update every 5 seconds
       }
       
                  // Create AbortController for timeout - adjust based on problem complexity
           const controller = new AbortController();
           const timeoutDuration = problem.length > 500 ? 180000 : 150000; // Extended timeout: 150s for simple, 180s for complex problems
       const timeoutId = setTimeout(() => {
         console.log(`DeepSeek API request timed out after ${timeoutDuration/1000} seconds`);
         controller.abort();
       }, timeoutDuration);
       
       const response = await this.retryRequest(async () => {
         return await fetch(this.baseUrl, {
           method: 'POST',
           headers: {
             'Content-Type': 'application/json',
             'Authorization': `Bearer ${this.apiKey}`,
           },
           body: JSON.stringify({
             model: 'deepseek-chat',
             messages: [
               {
                 role: 'system',
                 content: 'You are an expert HKDSE Mathematics educator and AI assistant. Your role is to analyze mathematical problems and create comprehensive knowledge graphs that match the EXACT structure and quality of HKDSE mathematics problems. You must create detailed knowledge graphs with: 1) Problem statement nodes, 2) Key mathematical concepts and principles, 3) Step-by-step solution process with intermediate calculations, 4) Specific formulas and methods used, 5) Intermediate numerical values, and 6) Final answers with verification. Each node must have proper id, name, type (concepts/formulas/values/steps), description, value, importance (1-10), and 3D coordinates. Each link must connect nodes logically with proper relationship types and strength ratings. Always respond with ONLY valid JSON format matching HKDSE sample structure.'
               },
               {
                 role: 'user',
                 content: prompt
               }
             ],
             temperature: 0.3,
             max_tokens: 4000, // Increased for more detailed HKDSE-style responses
           }),
           signal: controller.signal
         });
       });
       
       clearTimeout(timeoutId);
       if (progressUpdateInterval) {
         clearInterval(progressUpdateInterval);
       }
      console.log('DeepSeek API response received');

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek Think API Error Response:', errorText);
        
        let errorMessage = 'API request failed';
        if (response.status === 401) {
          errorMessage = 'Authentication failed. Please check API key.';
        } else if (response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please try again later.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again.';
        } else {
          errorMessage = `API error (${response.status}): ${errorText}`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('DeepSeek Think API Response:', data);
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw content from Think API:', content);
      
      // Get token usage from response
      if (data.usage) {
        inputTokens = data.usage.prompt_tokens || inputTokens;
        outputTokens = data.usage.completion_tokens || 0;
      }
      
      if (onProgress) {
        onProgress({
          status: 'generating',
          message: '正在生成知識圖譜...',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: Math.floor((inputTokens + outputTokens) * 0.3),
          cost: this.calculateCost(inputTokens, outputTokens),
          details: 'AI is analyzing problem and generating knowledge graph'
        });
      }
      
                   // Parse the JSON response
      let knowledgeGraphData;
      try {
        knowledgeGraphData = JSON.parse(content);
        console.log('Successfully parsed JSON:', knowledgeGraphData);
             } catch (parseError) {
         console.error('JSON Parse Error:', parseError);
         console.error('Raw content from API:', content);
         
         // Try to extract JSON from the response if it contains extra text
         try {
           // Look for JSON object in the response
           const jsonMatch = content.match(/\{[\s\S]*\}/);
           if (jsonMatch) {
             const extractedJson = jsonMatch[0];
             console.log('Attempting to parse extracted JSON:', extractedJson);
             knowledgeGraphData = JSON.parse(extractedJson);
             console.log('Successfully parsed extracted JSON:', knowledgeGraphData);
           } else {
             throw new Error('No valid JSON found in response');
           }
         } catch (extractError) {
           console.error('Failed to extract JSON:', extractError);
           // Use fallback knowledge graph instead of throwing error
           knowledgeGraphData = this.createFallbackKnowledgeGraph(problem, problemType);
           console.warn('Using fallback knowledge graph due to JSON parsing error.');
         }
       }
      
      // If we still don't have valid data, create a fallback
      if (!knowledgeGraphData || !knowledgeGraphData.graphData) {
         console.warn('No valid knowledge graph data, creating fallback...');
         knowledgeGraphData = this.createFallbackKnowledgeGraph(problem, problemType);
       }
      
      // Validate and fix the generated knowledge graph
      const validatedData = this.validateAndFixKnowledgeGraph(knowledgeGraphData, problem, problemType);
      
      if (onProgress) {
        onProgress({
          status: 'validating',
          message: '驗證知識圖譜完整性...',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: inputTokens + outputTokens,
          cost: this.calculateCost(inputTokens, outputTokens),
          details: 'Validating JSON structure and data integrity'
        });
      }
      
      const costIncurred = this.calculateCost(inputTokens, outputTokens);
      
      if (onProgress) {
        onProgress({
          status: 'complete',
          message: '知識圖譜生成完成！',
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          tokensUsed: inputTokens + outputTokens,
          cost: costIncurred,
          details: `Analysis completed successfully using ${problemCategory} template`
        });
      }
      
      return {
        ...validatedData,
        metadata: {
          tokensUsed: inputTokens + outputTokens,
          generationTime: Math.floor((Date.now() - startTime) / 1000),
          apiResponse: data,
          validationApplied: true,
          isRealAIGenerated: true,
          costIncurred: costIncurred
        }
      };
         } catch (error) {
       console.error('Error in thinkAboutProblem:', error);
       
       // Clean up any ongoing intervals
       if (progressUpdateInterval) {
         clearInterval(progressUpdateInterval);
       }
       
              // Handle specific error types
       let errorMessage = 'An error occurred during analysis';
                if (error.name === 'AbortError') {
           const timeoutDuration = problem.length > 500 ? 180000 : 150000; // Extended timeout values
           errorMessage = `Request timed out after ${timeoutDuration/1000} seconds. Please try again with a simpler problem or check your internet connection.`;
       } else if (error.message.includes('timeout')) {
         errorMessage = 'Request timed out. Please try again with a simpler problem or check your internet connection.';
       } else if (error.message.includes('network') || error.message.includes('fetch')) {
         errorMessage = 'Network error. Please check your internet connection and try again.';
       } else if (error.message.includes('API') && error.message.includes('connection')) {
         errorMessage = 'DeepSeek API connection failed. Please check your internet connection and try again.';
       } else if (error.message) {
         errorMessage = error.message;
       }
       
               // Update progress - error
        if (onProgress) {
          onProgress({
            status: 'error',
            message: errorMessage,
            timeElapsed: Math.floor((Date.now() - startTime) / 1000),
            tokensUsed: inputTokens + outputTokens,
            cost: this.calculateCost(inputTokens, outputTokens),
            error: errorMessage
          });
        }
       
       throw new Error(errorMessage);
     }
  }

         createThinkPrompt(problem, problemType, jsonStructure = null) {
    // Determine if this is an HKDSE problem or Topics problem - improved detection
    let isHKDSE = false;
    let problemCategory = 'Topics';
    
    if (jsonStructure) {
      // Check multiple indicators for HKDSE problems
      isHKDSE = (
        (jsonStructure.problem && jsonStructure.problem.includes('HKDSE')) ||
        (jsonStructure.analysis && jsonStructure.analysis.includes('HKDSE')) ||
        (jsonStructure.problemType && jsonStructure.problemType.includes('HKDSE')) ||
        (jsonStructure.id && jsonStructure.id.includes('hkdse')) ||
        (jsonStructure.graphData && jsonStructure.graphData.nodes && 
         jsonStructure.graphData.nodes.some(node => 
           node.name && node.name.includes('HKDSE')
         ))
      );
      problemCategory = isHKDSE ? 'HKDSE' : 'Topics';
    }
    
    // Use the actual JSON structure as a template if available
    const templateStructure = jsonStructure ? JSON.stringify(jsonStructure, null, 2) : null;
    
    let basePrompt = `You are an expert ${problemCategory} Mathematics educator and AI assistant. Your task is to analyze the following mathematical problem and create a comprehensive knowledge graph that matches the EXACT structure and quality of ${problemCategory} mathematics problems.

PROBLEM: ${problem}
PROBLEM TYPE: ${problemType}
PROBLEM CATEGORY: ${problemCategory}

`;

    if (templateStructure) {
      basePrompt += `TEMPLATE JSON STRUCTURE (Use this EXACT format for your response):
${templateStructure}

IMPORTANT: Your response must follow the EXACT structure above, but with content specific to the given problem. Replace the template content with actual analysis for the problem: "${problem}"

REQUIREMENTS:
- Use the EXACT same node IDs, names, and structure as the template
- Replace template values with actual problem-specific content
- Maintain the same 3D coordinates and relationship structure
- Keep the same importance ratings and node types
- Ensure all mathematical expressions are correctly formatted in LaTeX
- Show detailed step-by-step solution with intermediate calculations
- Include both problem-solving approach and underlying concepts
- Match the template quality and structure exactly

CRITICAL: Return ONLY valid JSON following the template structure above. No markdown, no code blocks, no explanatory text.`;
    } else {
      basePrompt += `Create a knowledge graph that follows this EXACT ${problemCategory} structure with these REQUIRED nodes:

${isHKDSE ? `
1. **Problem Statement**: The mathematical problem itself
2. **Solution Methods**: Available methods to solve this type of problem (e.g., "Factoring, Quadratic Formula, Completing the Square")
3. **Step 1: Problem Analysis**: Analyze the problem structure and identify key elements
4. **Step 2: Method Selection**: Choose the most appropriate solution method
5. **Step 3: Perform Calculations**: Execute the mathematical calculations
6. **Step 4: Verify Solution**: Check the solution for correctness
7. **Intermediate Values**: Key numerical results from calculations
8. **Final Answer**: The complete solution to the problem
9. **HKDSE-Specific Concepts**: Concepts specific to HKDSE examination format
` : `
1. **Problem Statement**: The mathematical problem itself
2. **Key Mathematical Concepts**: Fundamental concepts and principles involved
3. **Solution Method**: Mathematical method to solve this problem
4. **Step 1: Problem Analysis**: Analyze the problem structure and identify key elements
5. **Step 2: Choose Method**: Select appropriate mathematical method
6. **Step 3: Perform Calculations**: Execute the mathematical calculations
7. **Step 4: Verify Solution**: Check the solution for correctness
8. **Intermediate Values**: Key numerical results from calculations
9. **Final Answer**: The complete solution to the problem
`}

For each node, provide:
- id: descriptive identifier (e.g., "problem_statement", "solution_methods", "results_verification")
- name: clear, descriptive name matching the structure above
- type: "concepts", "formulas", "values", or "steps"
- description: detailed explanation
- value: mathematical expression, numerical value, or detailed step description
- importance: 1-10 rating (10 for most important like Problem Statement and Results)
- x, y, z: 3D coordinates (distribute logically in 3D space)

For relationships, provide:
- from: source node id
- to: target node id
- type: relationship type ("enables", "leads to", "contributes to", "calculates")
- strength: 1-10 rating

CRITICAL: Respond with ONLY valid JSON in this exact format:
{
  "problem": "${problem}",
  "problemType": "${problemType}",
  "analysis": "Comprehensive ${problemCategory}-style analysis with detailed solution methods, step-by-step solution process, and complete verification of results",
  "graphData": {
    "nodes": [
      {
        "id": "problem_statement",
        "name": "Problem Statement",
        "type": "concepts",
        "description": "The mathematical problem to be solved",
        "value": "${problem}",
        "importance": 10,
        "x": 0, "y": -200, "z": 0
      }
    ],
    "links": []
  }
}

REQUIREMENTS:
- MUST include ALL required node types listed above for ${problemCategory}
- Include 15-30 relationships showing logical flow
- Use precise mathematical notation and LaTeX formatting
- Show detailed step-by-step solution with intermediate calculations
- Include both problem-solving approach and underlying concepts
- Match ${problemCategory} sample quality and structure exactly
- Ensure all mathematical expressions are correctly formatted
- Create logical 3D distribution for visualization
- Focus on appropriate key elements for ${problemCategory}

IMPORTANT: Return ONLY pure JSON. No markdown, no code blocks, no explanatory text.`;
    }

    return basePrompt;
  }

  validateAndFixKnowledgeGraph(knowledgeGraphData, problem, problemType) {
    if (!knowledgeGraphData || typeof knowledgeGraphData !== 'object') {
      throw new Error('API returned invalid data structure');
    }
    
    if (!knowledgeGraphData.graphData || !knowledgeGraphData.graphData.nodes) {
      console.error('Invalid knowledge graph data structure:', knowledgeGraphData);
      throw new Error('API response missing knowledge graph structure');
    }
    
    if (knowledgeGraphData.graphData.nodes.length === 0) {
      throw new Error('API generated no knowledge graph nodes');
    }

    // Ensure required fields exist
    const validatedData = {
      problem: knowledgeGraphData.problem || problem,
      problemType: knowledgeGraphData.problemType || problemType,
      analysis: knowledgeGraphData.analysis || `Analysis of ${problemType} problem: ${problem}`,
      graphData: {
        nodes: this.validateAndFixNodes(knowledgeGraphData.graphData.nodes || []),
        links: this.validateAndFixLinks(knowledgeGraphData.graphData.links || [])
      },
      metadata: {
        ...knowledgeGraphData.metadata,
        validationApplied: true,
        validationTimestamp: new Date().toISOString(),
        isRealAIGenerated: true
      }
    };

    return validatedData;
  }

  validateAndFixNodes(nodes) {
    if (!Array.isArray(nodes)) {
      throw new Error('Invalid nodes structure from API');
    }

    return nodes.map((node, index) => {
      if (!node || typeof node !== 'object') {
        throw new Error(`Invalid node structure for node ${index}`);
      }

      return {
        id: node.id || `node_${index}`,
        name: node.name || `Node ${index}`,
        type: node.type || 'concepts',
        description: node.description || 'Node description',
        value: node.value || '',
        importance: node.importance || 5,
        x: node.x || 0,
        y: node.y || 0,
        z: node.z || 0
      };
    });
  }

  validateAndFixLinks(links) {
    if (!Array.isArray(links)) {
      return [];
    }

    return links.map((link, index) => {
      if (!link || typeof link !== 'object') {
        return null;
      }

      return {
        from: link.from || `node_0`,
        to: link.to || `node_1`,
        type: link.type || 'relationship',
        strength: link.strength || 5
      };
    }).filter(link => link !== null);
  }

   // Create a fallback knowledge graph when API fails
   createFallbackKnowledgeGraph(problem, problemType) {
     console.log('Creating fallback knowledge graph for:', problem);
     
     // Determine problem-specific content based on problemType
     const getProblemSpecificContent = () => {
       switch (problemType) {
         case 'quadratic':
           return {
             solutionMethods: "Factoring, Quadratic Formula, Completing the Square",
             keyConcepts: "Quadratic equations, roots, discriminant, vertex form",
             step1: "Identify coefficients: a, b, c in ax² + bx + c = 0",
             step2: "Calculate discriminant: Δ = b² - 4ac",
             step3: "Choose appropriate method based on discriminant value",
             step4: "Apply chosen method to find roots",
             verification: "Substitute roots back into original equation"
           };
         case 'circle_geometry':
           return {
             solutionMethods: "Standard form, General form, Center-radius form",
             keyConcepts: "Circle equations, center, radius, distance formula",
             step1: "Identify center coordinates (h, k) and radius r",
             step2: "Use standard form: (x - h)² + (y - k)² = r²",
             step3: "Expand and simplify to general form if needed",
             step4: "Verify center and radius from equation",
             verification: "Check that all points satisfy the equation"
           };
         case 'factor_remainder':
           return {
             solutionMethods: "Factor Theorem, Remainder Theorem, Synthetic Division",
             keyConcepts: "Polynomials, factors, remainders, roots",
             step1: "Identify polynomial degree and coefficients",
             step2: "Use Factor Theorem to find potential factors",
             step3: "Apply synthetic division or long division",
             step4: "Verify factors and calculate remainders",
             verification: "Multiply factors to confirm original polynomial"
           };
         case 'probability':
           return {
             solutionMethods: "Counting principles, Probability formulas, Tree diagrams",
             keyConcepts: "Probability, events, sample space, combinations",
             step1: "Define sample space and identify favorable outcomes",
             step2: "Apply probability formula: P(A) = n(A)/n(S)",
             step3: "Use counting principles if needed",
             step4: "Check probability is between 0 and 1",
             verification: "Sum of all probabilities should equal 1"
           };
         case 'statistics':
           return {
             solutionMethods: "Mean, Median, Mode, Standard Deviation",
             keyConcepts: "Central tendency, dispersion, data analysis",
             step1: "Organize data in ascending order",
             step2: "Calculate mean: sum of values ÷ number of values",
             step3: "Find median: middle value or average of two middle values",
             step4: "Identify mode: most frequent value",
             verification: "Check calculations and interpret results"
           };
         case 'trigonometry':
           return {
             solutionMethods: "SOHCAHTOA, Unit Circle, Trigonometric Identities",
             keyConcepts: "Sine, cosine, tangent, angles, ratios",
             step1: "Identify given angle and required trigonometric function",
             step2: "Use appropriate trigonometric ratio or identity",
             step3: "Apply calculator or unit circle values",
             step4: "Simplify and rationalize if needed",
             verification: "Check answer using inverse trigonometric functions"
           };
         default:
           return {
             solutionMethods: "General mathematical problem-solving techniques",
             keyConcepts: "Mathematical concepts and principles",
             step1: "Analyze the problem structure and identify key elements",
             step2: "Select appropriate mathematical method",
             step3: "Execute the mathematical calculations",
             step4: "Verify the solution for correctness",
             verification: "Check calculations and logical consistency"
           };
       }
     };

     const content = getProblemSpecificContent();
     
     return {
       problem: problem,
       problemType: problemType,
       analysis: `Comprehensive HKDSE-style analysis of ${problemType} problem: ${problem}. This analysis includes detailed solution methods, step-by-step solution process, and complete verification of results.`,
       graphData: {
         nodes: [
           {
             id: "problem_statement",
             name: "Problem Statement",
             type: "concepts",
             description: "The mathematical problem to be solved",
             value: problem,
             importance: 10,
             x: 0, y: -200, z: 0
           },
           {
             id: "problem_type",
             name: "Problem Type",
             type: "concepts",
             description: "Classification of the mathematical problem",
             value: problemType,
             importance: 8,
             x: -150, y: -100, z: 20
           },
           {
             id: "key_concepts",
             name: "Key Mathematical Concepts",
             type: "concepts",
             description: "Fundamental concepts and principles involved in this problem type",
             value: content.keyConcepts,
             importance: 9,
             x: 150, y: -100, z: 20
           },
           {
             id: "solution_methods",
             name: "Solution Methods",
             type: "concepts",
             description: "Available methods to solve this type of problem",
             value: content.solutionMethods,
             importance: 9,
             x: -120, y: 50, z: -15
           },
           {
             id: "step1_analysis",
             name: "Step 1: Problem Analysis",
             type: "steps",
             description: "Analyze the problem structure and identify key elements",
             value: content.step1,
             importance: 8,
             x: 120, y: 50, z: -15
           },
           {
             id: "step2_method",
             name: "Step 2: Choose Method",
             type: "steps",
             description: "Select appropriate mathematical method based on problem type",
             value: content.step2,
             importance: 9,
             x: -100, y: 150, z: 40
           },
           {
             id: "step3_calculation",
             name: "Step 3: Perform Calculations",
             type: "steps",
             description: "Execute the mathematical calculations using chosen method",
             value: content.step3,
             importance: 9,
             x: 100, y: 150, z: 40
           },
           {
             id: "step4_verification",
             name: "Step 4: Verify Solution",
             type: "steps",
             description: "Check the solution for correctness and completeness",
             value: content.step4,
             importance: 8,
             x: 0, y: 200, z: -20
           },
           {
             id: "intermediate_values",
             name: "Intermediate Values",
             type: "values",
             description: "Key numerical results from calculations",
             value: "Important intermediate results from the solving process",
             importance: 7,
             x: -80, y: 250, z: 0
           },
           {
             id: "results_verification",
             name: "Results & Verification",
             type: "values",
             description: "Final results with complete verification",
             value: `Final answer with verification: ${content.verification}`,
             importance: 10,
             x: 80, y: 250, z: 0
           },
           {
             id: "solution_steps",
             name: "Solution Steps",
             type: "steps",
             description: "Complete step-by-step solution process",
             value: `1. ${content.step1}\n2. ${content.step2}\n3. ${content.step3}\n4. ${content.step4}`,
             importance: 10,
             x: 0, y: 100, z: 0
           }
         ],
         links: [
           { from: "problem_statement", to: "problem_type", type: "enables", strength: 8 },
           { from: "problem_type", to: "key_concepts", type: "enables", strength: 9 },
           { from: "key_concepts", to: "solution_methods", type: "enables", strength: 9 },
           { from: "solution_methods", to: "step1_analysis", type: "leads to", strength: 8 },
           { from: "step1_analysis", to: "step2_method", type: "leads to", strength: 9 },
           { from: "step2_method", to: "step3_calculation", type: "leads to", strength: 9 },
           { from: "step3_calculation", to: "intermediate_values", type: "calculates", strength: 8 },
           { from: "step3_calculation", to: "step4_verification", type: "leads to", strength: 8 },
           { from: "step4_verification", to: "results_verification", type: "leads to", strength: 10 },
           { from: "intermediate_values", to: "results_verification", type: "contributes to", strength: 9 },
           { from: "solution_methods", to: "solution_steps", type: "enables", strength: 10 },
           { from: "solution_steps", to: "results_verification", type: "calculates", strength: 10 }
         ]
       }
     };
   }

   // Helper method to format time duration
   formatDuration(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Helper method to format cost
  formatCost(cost) {
    return `¥${cost.toFixed(4)}`;
  }

  // Test DeepSeek API connection
  async testConnection() {
    try {
      console.log('Testing DeepSeek API connection...');
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: 'Hello'
            }
          ],
          max_tokens: 10
        })
      });
      
      if (response.ok) {
        console.log('DeepSeek API connection successful');
        return { success: true, message: 'DeepSeek API connected successfully' };
      } else {
        const errorText = await response.text();
        console.error('DeepSeek API connection failed:', errorText);
        return { success: false, message: `Connection failed: ${response.status} - ${errorText}` };
      }
    } catch (error) {
      console.error('DeepSeek API connection error:', error);
      return { success: false, message: `Connection error: ${error.message}` };
    }
  }

  // Test timeout handling
  async testTimeoutHandling() {
    console.log('Testing timeout handling...');
    try {
      const result = await this.thinkAboutProblem(
        'Test timeout with a very complex mathematical problem that should trigger timeout',
        'general',
        (progress) => {
          console.log('Progress:', progress);
        }
      );
      console.log('Timeout test completed successfully:', result);
      return true;
    } catch (error) {
      console.log('Timeout test failed as expected:', error.message);
      return false;
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

const deepSeekThinkService = new DeepSeekThinkService();
export default deepSeekThinkService; 